import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { validateSignupForm } from "../utils/formValidation";
import { Box, Button, CircularProgress, IconButton } from "@mui/material";
import {
  AUTH_FIELD_BG,
  AuthFieldLabel,
  AuthLink,
  AuthPage,
  AuthStagger,
  authSubmitSx,
} from "../components/auth/AuthLayout";
import CustomTextField from "../components/CustomTextField";
import CountryCodePhoneField from "../components/CountryCodePhoneField";
import { signup } from "../services/authService";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [organization_name, setOrganization_name] = useState("");
  const [time_zone, setTime_zone] = useState(""); 
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{
    fullName?: string;
    email?: string;
    phone?: string;
    password?: string;
    organization_name?: string;
    time_zone?: string;
    confirmPassword?: string;
  }>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const validationResults = validateSignupForm({
      fullName,
      email,
      phone: phoneNumber,
      password,
      confirmPassword,
    });
    const newErrors: { [key: string]: string } = {};
    Object.entries(validationResults).forEach(([key, result]) => {
      if (!result.valid && result.error) {
        newErrors[key] = result.error;
      }
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setLoading(false);
      return;
    }

    try {
      console.log("Submitting signup with:   ")
      const result = await signup({
  fullName,
  email,
  password,
  organizationName: organization_name, 
  country: countryCode,
  phone: phoneNumber,
});

      if (result.success) {
        const successMessage =
          (typeof result.message === "string" && result.message.trim()) ||
          (typeof (result as any)?.user?.message === "string" &&
            (result as any).user.message.trim()) ||
          (typeof (result as any)?.user?.msg === "string" &&
            (result as any).user.msg.trim()) ||
          "Account created successfully!";
        toast.success(successMessage, {
          position: "top-right",
        });
        navigate("/home");
      } else {
        const errorMessage =
          (typeof result.message === "string" && result.message.trim()) ||
          (typeof result.error === "string" && result.error.trim()) ||
          result.error ||
          "Signup failed";
        toast.error(errorMessage, {
          position: "top-right",
        });
      }
    } catch (error) {
      console.error("Signup error:", error);
      const message =
        (error as any)?.response?.data?.message ||
        (error as any)?.response?.data?.error ||
        (error as any)?.message ||
        "An unexpected error occurred";
      toast.error(message, {
        position: "top-right",
        toastId: String(message),
      });
    } finally {
      setLoading(false);
    }
  };

  function onCountryCodeChange(value: string): void {
    setCountryCode(value);
    setErrors((prev) => ({ ...prev, phone: undefined }));
  }
  function onPhoneNumberChange(value: string): void {
    setPhoneNumber(value);
    setErrors((prev) => ({ ...prev, phone: undefined }));
  }
  const passwordToggle = (
    shown: boolean,
    toggle: () => void,
    label: string
  ) => (
    <IconButton
      aria-label={shown ? `Hide ${label}` : `Show ${label}`}
      onClick={toggle}
      onMouseDown={(e) => e.preventDefault()}
      edge="end"
    >
      {shown ? <VisibilityOff /> : <Visibility />}
    </IconButton>
  );

  return (
    <AuthPage
      title="Create your account"
      subtitle="Set up your organization in a minute."
      footer={
        <>
          Already have an account? <AuthLink to="/login">Log in</AuthLink>
        </>
      }
    >
      <AuthStagger component="form" startMs={120} onSubmit={handleSubmit} sx={{ width: "100%" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            columnGap: 2,
          }}
        >
          <Box>
            <AuthFieldLabel>Full Name</AuthFieldLabel>
            <CustomTextField
              label=""
              type="text"
              value={fullName}
              bgColor={AUTH_FIELD_BG}
              placeholder="Jane Doe"
              autoComplete="name"
              onChange={(e) => {
                setFullName(e.target.value);
                setErrors((prev) => ({ ...prev, fullName: undefined }));
              }}
              required
              fullWidth
              sx={{ mb: 2 }}
              error={!!errors.fullName}
              helperText={errors.fullName}
            />
          </Box>
          <Box>
            <AuthFieldLabel>Organization Name</AuthFieldLabel>
            <CustomTextField
              label=""
              type="text"
              value={organization_name}
              bgColor={AUTH_FIELD_BG}
              placeholder="Your club or company"
              autoComplete="organization"
              onChange={(e) => {
                setOrganization_name(e.target.value);
                setErrors((prev) => ({ ...prev, organization_name: undefined }));
              }}
              required
              fullWidth
              sx={{ mb: 2 }}
              error={!!errors.organization_name}
              helperText={errors.organization_name}
            />
          </Box>
        </Box>
        <AuthFieldLabel>Email</AuthFieldLabel>
        <CustomTextField
          label=""
          type="email"
          value={email}
          bgColor={AUTH_FIELD_BG}
          placeholder="you@company.com"
          autoComplete="email"
          onChange={(e) => {
            setEmail(e.target.value);
            setErrors((prev) => ({ ...prev, email: undefined }));
          }}
          required
          fullWidth
          sx={{ mb: 2 }}
          error={!!errors.email}
          helperText={errors.email}
        />
        <Box sx={{ mb: 2 }}>
          <CountryCodePhoneField
            countryCode={countryCode}
            phoneNumber={phoneNumber}
            onCountryCodeChange={onCountryCodeChange}
            onPhoneNumberChange={onPhoneNumberChange}
            error={!!errors.phone}
          />
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            columnGap: 2,
          }}
        >
          <Box>
            <AuthFieldLabel>Password</AuthFieldLabel>
            <CustomTextField
              label=""
              type={showPassword ? "text" : "password"}
              value={password}
              bgColor={AUTH_FIELD_BG}
              autoComplete="new-password"
              onChange={(e) => {
                setPassword(e.target.value);
                setErrors((prev) => ({ ...prev, password: undefined }));
              }}
              required
              fullWidth
              sx={{ mb: 2 }}
              error={!!errors.password}
              helperText={errors.password}
              endIcon={passwordToggle(
                showPassword,
                () => setShowPassword((prev) => !prev),
                "password"
              )}
            />
          </Box>
          <Box>
            <AuthFieldLabel>Confirm Password</AuthFieldLabel>
            <CustomTextField
              label=""
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              bgColor={AUTH_FIELD_BG}
              autoComplete="new-password"
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
              }}
              required
              fullWidth
              sx={{ mb: 2 }}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              endIcon={passwordToggle(
                showConfirmPassword,
                () => setShowConfirmPassword((prev) => !prev),
                "confirm password"
              )}
            />
          </Box>
        </Box>
        <Button
          variant="contained"
          type="submit"
          fullWidth
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={18} color="inherit" /> : undefined
          }
          sx={{ ...authSubmitSx, mt: 1.5 }}
        >
          {loading ? "Creating account..." : "Create account"}
        </Button>
      </AuthStagger>
    </AuthPage>
  );
};

export default Signup;
